/**
 * Whether the visitor has asked for Easy Read, and which page they asked on.
 *
 * Replaces the aXread panel store. aXread is decommissioned: Easy Read is now a VERSION OF THE
 * PAGE (EasyRead.dc.html), not an overlay over it, so what has to be shared across the tree is
 * one boolean - the header button sets it, the shell reads it, and both report it.
 *
 * Remembered under its own key so a visitor who had the old overlay open does not inherit it.
 */

type Listener = (isOn: boolean) => void;

const STORAGE_KEY = 'axai-easy-read';

let isOn = false;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l(isOn));
}

function remember(next: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
  } catch {
    /* private window, or site data blocked: the choice still applies to this page */
  }
}

export const easyRead = {
  getIsOn: (): boolean => isOn,

  /** Read the remembered choice. Safe to call more than once. */
  restore(): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    const next = stored === 'on';
    if (next === isOn) return;
    isOn = next;
    emit();
  },

  set(next: boolean): void {
    remember(next);
    if (next === isOn) return;
    isOn = next;
    emit();
  },

  toggle(): boolean {
    this.set(!isOn);
    return isOn;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
